import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, Loader2, AlertCircle, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminLogin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setAuth, user, isAdmin } = useAuth();
  const { settings: siteSettings } = useSite();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Redirect if already logged in as admin
  useEffect(() => {
    if (user && isAdmin) {
      navigate('/admin');
    }
  }, [user, isAdmin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });

      const { access_token, user: userData } = res.data;

      // Check if user is admin
      if (!userData.is_admin) {
        setError(t('auth.accessDenied'));
        setLoading(false);
        return;
      }

      setAuth(access_token, userData);
      toast.success(t('auth.welcomeAdmin'));
      navigate('/admin');
    } catch (err) {
      console.error('Admin login error:', err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError(t('auth.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Admin keeps a fixed dark theme (independent of public site colors)
  const primaryColor = '#6366f1';
  const backgroundColor = '#0a0a0f';

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden"
      style={{ backgroundColor }}
    >
      {/* Background effects */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 30%, ${primaryColor}15, transparent 50%)` }}
      />
      
      <motion.div
        className="w-full max-w-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex flex-col items-center gap-3 mb-4">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ 
                background: `linear-gradient(to bottom right, #6366f1, #4f46e5)`,
                boxShadow: `0 20px 40px -10px rgba(99, 102, 241, 0.5)`
              }}
            >
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white font-['Chakra_Petch'] uppercase tracking-wider">
              Admin Panel
            </h1>
          </div>
          <p className="text-zinc-500 text-sm">
            {t('auth.loginCredentials')}
          </p>
        </div>

        {/* Login Card */}
        <div 
          className="rounded-2xl p-6 border"
          style={{ 
            backgroundColor: siteSettings?.colors?.card || '#18181b',
            borderColor: siteSettings?.colors?.card_border || '#27272a'
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error Message */}
            {error && (
              <motion.div 
                className="p-3 rounded-lg bg-cyan-900/30 border border-cyan-900/50 flex items-start gap-2"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <AlertCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-cyan-400 text-sm">{error}</p>
              </motion.div>
            )}

            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 bg-zinc-900/50 border-zinc-700 text-white placeholder:text-zinc-500"
                  required
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 bg-zinc-900/50 border-zinc-700 text-white placeholder:text-zinc-500"
                  required
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-base font-semibold rounded-xl transition-all mt-6"
              style={{ 
                background: `linear-gradient(to right, #6366f1, #4f46e5)`,
                boxShadow: loading ? 'none' : `0 10px 30px -5px rgba(99, 102, 241, 0.5)`
              }}
              data-testid="admin-login-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {t('auth.loggingIn')}
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5 mr-2" />
                  {t('auth.login')}
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <a 
            href="/"
            className="text-zinc-500 text-sm hover:text-zinc-400"
          >
            ← {t('common.back')} {t('nav.home')}
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
